<template>
    <v-container fluid class="fill-height align-start">
        <v-row class="fill-height">
            <v-col cols="12" class="d-flex flex-column fill-height">
                <div class="d-flex align-center mb-4 flex-shrink-0">
                    <div>
                        <div class="text-h6">Skill Amplification</div>
                        <div class="text-caption text-grey">
                            Pick skills and the statuses that amplify them. The Analysis skill breakdown then shows how
                            many of each skill's hits had each status up, and how many had all of them (or any).
                        </div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-fade-transition>
                        <span v-if="saving" class="text-caption text-grey mr-2">Saving...</span>
                    </v-fade-transition>
                    <v-btn color="primary" prepend-icon="mdi-plus" size="small" @click="addEntry">Add Entry</v-btn>
                </div>

                <v-card variant="outlined" class="flex-grow-1 overflow-y-auto" color="surface">
                    <v-card-text class="pa-4">
                        <div v-if="entries.length === 0" class="text-center text-grey py-8">
                            <v-icon size="48" color="grey-darken-2" class="mb-2">mdi-flash-outline</v-icon>
                            <div>No skill amplifications defined.</div>
                            <div class="text-caption">Example: Magnum Shot, amplified by Ranged Combat Skill Damage Increase.</div>
                        </div>

                        <v-expansion-panels v-else variant="accordion" multiple>
                            <v-expansion-panel v-for="(entry, index) in entries" :key="index">
                                <v-expansion-panel-title>
                                    <span class="font-weight-bold mr-2">{{ entry.name || 'Unnamed' }}</span>
                                    <span class="text-caption text-grey">
                                        ({{ entry.skills.length }} skills, {{ entry.statuses.length }} statuses)
                                    </span>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text>
                                    <v-row dense class="align-center mb-2">
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="entry.name" label="Name" density="compact"
                                                variant="outlined" hide-details @update:model-value="save" />
                                        </v-col>
                                        <v-col cols="12" sm="6" class="text-right">
                                            <v-btn color="error" variant="text" size="small" prepend-icon="mdi-delete"
                                                @click="removeEntry(index)">Delete Entry</v-btn>
                                        </v-col>
                                    </v-row>

                                    <v-divider class="mb-3"></v-divider>

                                    <!-- Skills -->
                                    <div class="text-subtitle-2 mb-2">Skills</div>
                                    <v-autocomplete :items="allSkills" item-title="name" item-value="id"
                                        label="Add skill" placeholder="Search skill..." prepend-inner-icon="mdi-magnify"
                                        variant="outlined" density="compact" hide-details clearable auto-select-first
                                        return-object :model-value="null" @update:model-value="(item: any) => addSkill(entry, item)">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="item.raw.name" :subtitle="`ID: ${item.raw.id}`">
                                                <template v-slot:prepend>
                                                    <v-avatar size="24" rounded="0" class="mr-2">
                                                        <v-img :src="getSkillIconUrl(item.raw.id, region)" cover>
                                                            <template v-slot:error><v-icon icon="mdi-help-circle-outline"></v-icon></template>
                                                        </v-img>
                                                    </v-avatar>
                                                </template>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                    <v-chip-group column class="mb-3">
                                        <v-chip v-for="skillId in entry.skills" :key="skillId" closable
                                            @click:close="removeSkill(entry, skillId)">
                                            <v-avatar start rounded="0">
                                                <v-img :src="getSkillIconUrl(skillId, region)" cover>
                                                    <template v-slot:error><v-icon icon="mdi-help-circle-outline"></v-icon></template>
                                                </v-img>
                                            </v-avatar>
                                            {{ skillName(skillId) }}
                                        </v-chip>
                                    </v-chip-group>

                                    <!-- Statuses -->
                                    <div class="text-subtitle-2 mb-1">Amplifying statuses</div>
                                    <div class="text-caption text-grey mb-2">
                                        Checked at each hit on the player (a buff on the attacker) or on the entity hit
                                        (a debuff on the target, e.g. the boss).
                                    </div>
                                    <div class="d-flex align-center" style="gap: 8px">
                                    <v-btn-toggle :model-value="addOn[index] ?? 'attacker'" @update:model-value="(v: any) => addOn[index] = v" mandatory density="compact" variant="outlined"
                                        divided color="primary" class="flex-shrink-0">
                                        <v-btn value="attacker" size="small" prepend-icon="mdi-account">On player</v-btn>
                                        <v-btn value="target" size="small" prepend-icon="mdi-target">On target</v-btn>
                                    </v-btn-toggle>
                                    <v-autocomplete :items="allStatuses" item-title="name" item-value="id"
                                        :label="addOn[index] === 'target' ? 'Add status on the target' : 'Add status on the player'"
                                        placeholder="Search status..." prepend-inner-icon="mdi-magnify"
                                        variant="outlined" density="compact" hide-details clearable auto-select-first
                                        return-object :model-value="null"
                                        @update:model-value="(item: any) => addStatus(entry, item, addOn[index] === 'target')">
                                        <template v-slot:item="{ props, item }">
                                            <v-list-item v-bind="props" :title="item.raw.name" :subtitle="`ID: ${item.raw.id}`">
                                                <template v-slot:prepend>
                                                    <v-avatar size="24" rounded="0" class="mr-2">
                                                        <v-img :src="getStatusIconUrl(item.raw.id)" cover>
                                                            <template v-slot:error><v-icon icon="mdi-help-circle-outline"></v-icon></template>
                                                        </v-img>
                                                    </v-avatar>
                                                </template>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                    </div>
                                    <div v-for="(st, si) in entry.statuses" :key="`${st.id}:${st.onTarget}`"
                                        class="d-flex align-center mt-2" style="gap: 8px">
                                        <v-avatar size="24" rounded="0">
                                            <v-img :src="getStatusIconUrl(st.id)" cover>
                                                <template v-slot:error><v-icon icon="mdi-help-circle-outline"></v-icon></template>
                                            </v-img>
                                        </v-avatar>
                                        <span class="flex-grow-1 text-truncate">{{ statusName(st.id) }}</span>
                                        <v-btn-toggle :model-value="st.onTarget ? 'target' : 'attacker'" mandatory
                                            density="compact" variant="outlined" divided color="primary"
                                            @update:model-value="(v: string) => setOnTarget(entry, si, v === 'target')">
                                            <v-btn value="attacker" size="small" prepend-icon="mdi-account">On player</v-btn>
                                            <v-btn value="target" size="small" prepend-icon="mdi-target">On target</v-btn>
                                        </v-btn-toggle>
                                        <v-btn icon="mdi-close" size="small" variant="text" @click="removeStatus(entry, si)" />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { condNameMap, skillNameMap, region, skillAmpConfig, SkillAmpEntry } from '@/store';
import { saveConfig } from '@/apicall';
import { getSkillIconUrl, getStatusIconUrl } from '@/util';

// Config > Skill Amplification (stored server-side as skill_amplification;
// the backend counts coverage per hit, see cmd/dilmeterapi/skillAmp.go).
export default defineComponent({
    name: 'SkillAmplificationConfig',
    setup() {
        const copy = (v: SkillAmpEntry[]) => JSON.parse(JSON.stringify(v)) as SkillAmpEntry[];
        const entries = ref<SkillAmpEntry[]>(copy(skillAmpConfig.value));
        // The config may load after this tab first renders.
        watch(skillAmpConfig, v => { if (!saving.value) entries.value = copy(v); });

        const saving = ref(false);
        let timer: ReturnType<typeof setTimeout> | null = null;
        const save = () => {
            skillAmpConfig.value = copy(entries.value);
            saving.value = true;
            if (timer) clearTimeout(timer);
            timer = setTimeout(async () => {
                try {
                    await saveConfig('skill_amplification', entries.value);
                } catch (e) {
                    console.error('Auto-save skill amplification failed', e);
                } finally {
                    saving.value = false;
                }
            }, 1000);
        };

        const toItems = (m: Record<number, string>) => Object.entries(m)
            .map(([id, name]) => ({ id: Number(id), name: `${name} (${id})`, rawName: name }))
            .sort((a, b) => a.rawName.localeCompare(b.rawName));
        const allSkills = computed(() => toItems(skillNameMap.value));
        const allStatuses = computed(() => toItems(condNameMap.value));
        const skillName = (id: number) => skillNameMap.value[id] || `Unknown (${id})`;
        const statusName = (id: number) => condNameMap.value[id] || `Unknown (${id})`;

        const addEntry = () => {
            entries.value.push({ name: 'New Amplification', skills: [], statuses: [] });
            save();
        };
        const removeEntry = (i: number) => {
            if (!confirm('Delete this skill amplification entry?')) return;
            entries.value.splice(i, 1);
            addOn.value = {};
            save();
        };
        const addSkill = (entry: SkillAmpEntry, item: any) => {
            if (!item || entry.skills.includes(item.id)) return;
            entry.skills.push(item.id);
            save();
        };
        const removeSkill = (entry: SkillAmpEntry, id: number) => {
            entry.skills = entry.skills.filter(s => s !== id);
            save();
        };
        // Where the next status added to each entry is checked (by entry index).
        const addOn = ref<Record<number, 'attacker' | 'target'>>({});
        const addStatus = (entry: SkillAmpEntry, item: any, onTarget = false) => {
            if (!item || entry.statuses.some(s => s.id === item.id && s.onTarget === onTarget)) return;
            entry.statuses.push({ id: item.id, onTarget });
            save();
        };
        const removeStatus = (entry: SkillAmpEntry, i: number) => {
            entry.statuses.splice(i, 1);
            save();
        };
        const setOnTarget = (entry: SkillAmpEntry, i: number, onTarget: boolean) => {
            const st = entry.statuses[i];
            if (st.onTarget === onTarget) return;
            if (entry.statuses.some((s, j) => j !== i && s.id === st.id && s.onTarget === onTarget)) return;
            st.onTarget = onTarget;
            save();
        };

        return {
            entries, saving, save, addOn, allSkills, allStatuses, skillName, statusName, region,
            addEntry, removeEntry, addSkill, removeSkill, addStatus, removeStatus, setOnTarget,
            getSkillIconUrl, getStatusIconUrl,
        };
    },
});
</script>
